// LunaBell — top-nav Luna button that opens the Luna requests dropdown.
// design ref: components.jsx LunaPopover / LunaPopoverRow + the bell in TopNav.
//
// Clicking the bell opens a 420px popover with Active / Completed tabs and the
// requests grouped by recency (Today / Yesterday / Last week / month-year). This
// replaces navigating to the flat /luna/requests page. The full page still
// exists for deep links and the home card's "View all".

import {
  Box,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { differenceInCalendarDays, format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TbAlertCircleFilled,
  TbArrowRight,
  TbCircleCheckFilled,
  TbX,
} from "react-icons/tb";

import { LunaMark } from "components/ds/LunaMark";
import { useGetLunaRequests } from "state/luna";
import type { LunaRequest } from "services/luna/lunaApi";

type Tab = "active" | "completed";

// design ref: LunaPopoverRow active background — 117deg ice→indigo→coral wash
// over neutral.0.
const ACTIVE_ROW_BG =
  "linear-gradient(117deg, rgba(252,253,253,0) 30%, rgba(88,97,222,0.04) 65%, rgba(237,111,111,0.08) 100%), #FEFEFE";

const groupLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const days = differenceInCalendarDays(new Date(), d);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days <= 7) return "Last week";
  return format(d, "MMMM yyyy");
};

const formatRowDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "EEE, dd MMMM");
};

// Group a list into ordered [label, items] pairs, most recent first.
const groupByDate = (items: LunaRequest[]): [string, LunaRequest[]][] => {
  const sorted = [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
  const map = new Map<string, LunaRequest[]>();
  for (const r of sorted) {
    const label = groupLabel(r.updatedAt);
    const arr = map.get(label);
    if (arr) arr.push(r);
    else map.set(label, [r]);
  }
  return Array.from(map.entries());
};

const TabButton = ({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    flex="1"
    h="36px"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    gap="sm"
    fontSize="md"
    fontWeight={500}
    lineHeight="1"
    borderRadius="component.lg" // 8px
    bg={active ? "neutral.0" : "transparent"}
    color={active ? "neutral.950" : "neutral.700"}
    boxShadow={active ? "xs" : "none"}
    transition="background 120ms, color 120ms"
  >
    <Box
      as="span"
      minW="22px"
      px="1xs" // 6px
      py="1px"
      fontSize="xs" // 12px
      fontWeight={600}
      borderRadius="component.md" // 6px
      bg={active ? "neutral.100" : "neutral.200"}
      color="neutral.800"
    >
      {count}
    </Box>
    {label}
  </Box>
);

const LunaRow = ({
  request,
  isActive,
  onOpen,
}: {
  request: LunaRequest;
  isActive: boolean;
  onOpen: () => void;
}) => (
  <Flex
    align="center"
    gap="md" // 12px
    p="14px"
    borderRadius="component.2xl" // 12px
    boxShadow="elevLow"
    cursor="pointer"
    bg={isActive ? ACTIVE_ROW_BG : "neutral.0"}
    onClick={onOpen}
  >
    {/* Completed requests show Luna's done state; active ones her resting
        mark. Motion stays off — these are list rows, not status surfaces. */}
    <LunaMark size={22} animated={false} state={isActive ? "idle" : "done"} />
    <Box flex="1" minW={0}>
      <Text
        fontSize="md"
        fontWeight={600}
        color="neutral.950"
        mb="1xs"
        noOfLines={1}
      >
        {request.title || "New request"}
      </Text>
      <HStack spacing="sm" fontSize="smd" color="neutral.700" flexWrap="wrap">
        {isActive ? (
          <HStack spacing="2xs" color="warning.700" fontWeight={500}>
            <Icon as={TbAlertCircleFilled} boxSize="14px" color="warning.500" />
            Active
          </HStack>
        ) : (
          <HStack spacing="2xs" color="success.700" fontWeight={500}>
            <Icon as={TbCircleCheckFilled} boxSize="14px" color="success.600" />
            Completed
          </HStack>
        )}
        <Box as="span" color="neutral.400">
          ·
        </Box>
        <Box as="span">{formatRowDate(request.updatedAt)}</Box>
        <Box as="span" color="neutral.400">
          ·
        </Box>
        <HStack
          as="button"
          type="button"
          spacing="2xs"
          color="p.600"
          fontWeight={600}
          _hover={{ color: "p.700" }}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {isActive ? (
            <>
              Respond
              <Icon as={TbArrowRight} boxSize="14px" />
            </>
          ) : (
            "View history"
          )}
        </HStack>
      </HStack>
    </Box>
  </Flex>
);

const LunaBell = () => {
  const navigate = useNavigate();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [tab, setTab] = useState<Tab>("active");
  const { data } = useGetLunaRequests();

  const requests = data ?? [];
  const active = requests.filter((r) => r.status !== "closed");
  const completed = requests.filter((r) => r.status === "closed");
  const list = tab === "active" ? active : completed;
  const groups = groupByDate(list);

  const openSession = (sessionId: string) => {
    onClose();
    navigate(`/luna/requests/${sessionId}`);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
      gutter={8}
      isLazy
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label="Luna requests"
          onClick={onToggle}
          position="relative"
          w="36px"
          h="36px"
          borderRadius="component.lg"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.800"
          transition="background 120ms"
          bg={isOpen ? "neutral.100" : "transparent"}
          _hover={{ bg: "neutral.100" }}
        >
          <LunaMark size={18} animated={false} />
          {active.length > 0 && (
            <Box
              as="span"
              position="absolute"
              top="7px"
              right="8px"
              w="7px"
              h="7px"
              bg="error.500"
              borderRadius="full"
              border="1.5px solid"
              borderColor="neutral.25"
            />
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="420px"
          maxW="calc(100vw - 32px)"
          bg="neutral.0"
          borderWidth="1px"
          borderColor="neutral.200"
          borderRadius="component.4xl" // 16px
          boxShadow="overlay"
          zIndex={1500}
          _focusVisible={{ outline: "none", boxShadow: "overlay" }}
        >
          {/* Header */}
          <HStack
            spacing="10px"
            px="20px"
            py="lg"
            borderBottomWidth="1px"
            borderColor="neutral.200"
          >
            <LunaMark size={22} animated={false} />
            <Text
              fontFamily="heading"
              fontSize="18px"
              fontWeight={500}
              color="neutral.950"
            >
              Luna requests
            </Text>
            <Box
              as="button"
              type="button"
              aria-label="Close"
              ml="auto"
              p="2xs"
              color="neutral.700"
              borderRadius="component.md"
              _hover={{ bg: "neutral.100" }}
              onClick={onClose}
            >
              <Icon as={TbX} boxSize="16px" />
            </Box>
          </HStack>

          {/* Tabs */}
          <HStack
            spacing="2xs"
            p="2xs"
            mt="lg"
            mx="20px"
            mb="sm"
            bg="neutral.100"
            borderRadius="component.xl" // 10px
            borderWidth="1px"
            borderColor="neutral.200"
          >
            <TabButton
              label="Active"
              count={active.length}
              active={tab === "active"}
              onClick={() => setTab("active")}
            />
            <TabButton
              label="Completed"
              count={completed.length}
              active={tab === "completed"}
              onClick={() => setTab("completed")}
            />
          </HStack>

          {/* List */}
          <Box
            maxH="480px"
            overflowY="auto"
            pt="sm"
            px="20px"
            pb="20px"
            display="flex"
            flexDirection="column"
            gap="14px"
          >
            {groups.length === 0 ? (
              <Text
                fontSize="smd"
                color="neutral.600"
                textAlign="center"
                py="2xl"
              >
                {tab === "active"
                  ? "No active Luna requests"
                  : "No completed requests"}
              </Text>
            ) : (
              groups.map(([label, items]) => (
                <Box key={label}>
                  <Text
                    fontSize="smd"
                    fontWeight={500}
                    color="neutral.700"
                    px="2px"
                    py="sm"
                  >
                    {label}
                  </Text>
                  <VStack align="stretch" spacing="sm">
                    {items.map((r) => (
                      <LunaRow
                        key={r.sessionId}
                        request={r}
                        isActive={tab === "active"}
                        onOpen={() => openSession(r.sessionId)}
                      />
                    ))}
                  </VStack>
                </Box>
              ))
            )}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default LunaBell;
